import { useMemo } from 'react';

import { useColorModeValue } from '@ecoinvent/ui';
import { Edge, Node } from '@xyflow/react';

import { functionalChildIds } from 'hooks/data/datasets/V4/supplyChain/supplyChainUtils';
import { SupplyChainNode } from 'hooks/data/datasets/V4/supplyChain/types';
import { DerivedEdge, NodeCounts, ShownCounts } from 'hooks/data/datasets/V4/supplyChain/useSupplyChain';

import { computeSupplyChainPositions } from './computeSupplyChainPositions';
import { COLORS, SUPPLY_NODE_HEIGHT, SUPPLY_NODE_HEIGHT_IA, SUPPLY_V_SPACING } from './constants';
import { OverflowNodeData } from './OverflowNode';
import { SupplyChainEdgeData } from './SupplyChainEdge';
import { SupplyChainNodePayload } from './SupplyChainNode';
import { createColumnOccupancy } from './supplyChainOccupancy';

// Required: without it React Flow sets pointer-events:none on every node (hasPointerEvents) and the canvas goes dead.
const INTERACTIVE_NODE_STYLE: Node['style'] = Object.freeze({ pointerEvents: 'all', transition: 'opacity 0.2s' });

type Params = {
  nodes: SupplyChainNode[];
  edges: DerivedEdge[];
  nodeCounts: NodeCounts;
  shownCounts: ShownCounts;
  rootNodeId: string;
  expandedNodeIds: Set<string>;
  expandedOutputNodeIds: Set<string>;
  activeNodeId: string | null;
  mode: 'flows' | 'impact_assessment';
  selectedIndicatorId: string;
  // Bulk-fetched once in the canvas; keyed by the bare node id (`datasetId ?? id`).
  scoreByNodeId: Map<string, number | null>;
  scoreUnit: string;
  scoresLoading: boolean;
  scoresError: boolean;
  onExpand: (id: string) => void;
  onCollapse: (id: string) => void;
  onExpandOutputs: (id: string) => void;
  onCollapseOutputs: (id: string) => void;
  onSelectNode: (id: string) => void;
  onViewDataset: (id: string) => void;
  onCopyScore: (nodeId: string) => Promise<void>;
  showMoreInputs: (id: string) => void;
  showMoreOutputs: (id: string) => void;
};

type GraphLayout = { nodes: Node[]; edges: Edge[] };

export const useSupplyChainLayout = ({
  nodes,
  edges,
  nodeCounts,
  shownCounts,
  rootNodeId,
  expandedNodeIds,
  expandedOutputNodeIds,
  activeNodeId,
  mode,
  selectedIndicatorId,
  scoreByNodeId,
  scoreUnit,
  scoresLoading,
  scoresError,
  onExpand,
  onCollapse,
  onExpandOutputs,
  onCollapseOutputs,
  onSelectNode,
  onViewDataset,
  onCopyScore,
  showMoreInputs,
  showMoreOutputs,
}: Params): GraphLayout => {
  const overflowEdgeColor = useColorModeValue(COLORS.greenLight, COLORS.greenDark);

  return useMemo(() => {
    if (!nodes.find((n) => n.id === rootNodeId)) return { nodes: [], edges: [] };

    const nodeIds = nodes.map((n) => n.id);
    // Sole source of card height: the real cards, the overflow cards and the row pitch all read this.
    const nodeHeight = mode === 'impact_assessment' ? SUPPLY_NODE_HEIGHT_IA : SUPPLY_NODE_HEIGHT;
    const { positions, depth } = computeSupplyChainPositions(nodeIds, edges, rootNodeId, nodeHeight);

    // impact_assessment normalises amounts to a [2, 12] px stroke width; flows mode is a constant 2 px.
    const amounts = edges.filter((e) => e.amount != null).map((e) => e.amount as number);
    const maxAmt = amounts.length > 0 ? Math.max(...amounts) : 1;
    const minAmt = amounts.length > 0 ? Math.min(...amounts) : 0;
    const amtRange = maxAmt - minAmt || maxAmt || 1;
    const strokeWidthFor = (amount?: number) =>
      mode === 'flows' ? 2 : amount == null ? 2 : 2 + ((amount - minAmt) / amtRange) * 10;

    // Read from the edges, not from the node payload: a node's own `is_functional` is frozen at first
    // sight by `mergeNodes` and describes only the parent that first returned it.
    const functionalIds = functionalChildIds(edges);

    const flowNodes: Node[] = nodes.map((scNode) => {
      const counts = nodeCounts[scNode.id];
      const nodeDepth = depth[scNode.id] ?? 0;
      const nodeVariant = scNode.id === rootNodeId ? 'root' : nodeDepth > 0 ? 'input' : 'consumer';
      // Map the bulk result to the card's five states. `null` and an absent id both mean the indicator is
      // uncovered for this node, i.e. `empty`.
      const rawScore = scoreByNodeId.get(scNode.datasetId ?? scNode.id);
      const scoreStatus: SupplyChainNodePayload['scoreStatus'] = !selectedIndicatorId
        ? 'notAsked'
        : scoresError
          ? 'error'
          : scoresLoading
            ? 'pending'
            : typeof rawScore === 'number'
              ? 'ready'
              : 'empty';
      const payload: SupplyChainNodePayload = {
        node: scNode,
        nodeVariant,
        isRoot: scNode.id === rootNodeId,
        isActive: scNode.id === activeNodeId,
        isFunctional: functionalIds.has(scNode.id),
        isExpanded: expandedNodeIds.has(scNode.id),
        isOutputsExpanded: expandedOutputNodeIds.has(scNode.id),
        inputCount: counts?.inputs,
        outputCount: counts?.outputs,
        mode,
        nodeHeight,
        scoreStatus,
        score: typeof rawScore === 'number' ? rawScore : undefined,
        scoreUnit,
        onExpand: () => onExpand(scNode.id),
        onCollapse: () => onCollapse(scNode.id),
        onExpandOutputs: () => onExpandOutputs(scNode.id),
        onCollapseOutputs: () => onCollapseOutputs(scNode.id),
        onSelect: () => onSelectNode(scNode.id),
        onViewDataset: () => onViewDataset(scNode.datasetId ?? scNode.id),
        onCopyScore: () => onCopyScore(scNode.datasetId ?? scNode.id),
      };

      return {
        id: scNode.id,
        type: 'supplyChainNode',
        position: positions[scNode.id] ?? { x: 0, y: 0 },
        data: payload as unknown as Record<string, unknown>,
        draggable: false,
        style: INTERACTIVE_NODE_STYLE,
      };
    });

    // Total amount per target node — used to compute per-edge percentages.
    const totalByTarget: Record<string, number> = {};
    for (const e of edges) {
      if (e.amount != null) totalByTarget[e.target_id] = (totalByTarget[e.target_id] ?? 0) + e.amount;
    }

    const flowEdges: Edge[] = edges.map((e) => {
      const isInputEdge = (depth[e.source_id] ?? 0) > 0 || (depth[e.target_id] ?? 0) > 0;
      const total = totalByTarget[e.target_id];
      const label =
        e.amount != null
          ? mode === 'impact_assessment' && total
            ? `${((e.amount / total) * 100).toFixed(2)}%`
            : // trimStart covers the unreachable empty flow_object rather than rendering a leading space.
              `${e.flow_object} ${e.amount.toFixed(4)} ${e.unit}`.trimStart()
          : undefined;
      return {
        id: e.id,
        source: e.source_id,
        target: e.target_id,
        type: 'supplyChainEdge',
        data: {
          strokeWidth: strokeWidthFor(e.amount),
          edgeVariant: isInputEdge ? 'input' : 'consumer',
          isFunctional: e.is_functional,
          mode,
        } satisfies SupplyChainEdgeData,
        label,
      };
    });

    const overflowNodes: Node[] = [];
    const overflowEdges: Edge[] = [];
    const overflowEdgeStyle = {
      stroke: overflowEdgeColor,
      strokeDasharray: '5 4',
      strokeOpacity: 0.5,
      strokeWidth: 1.5,
      transition: 'opacity 0.2s',
    };

    // Seeded with every real node so overflow cards can't land on one.
    const pitch = nodeHeight + SUPPLY_V_SPACING;
    const { claimFreeY } = createColumnOccupancy(nodeIds.map((id) => positions[id]).filter(Boolean), pitch);

    for (const scNode of nodes) {
      const inputOverflow = (nodeCounts[scNode.id]?.inputs ?? 0) - (shownCounts[scNode.id]?.inputs ?? 0);

      if (inputOverflow > 0 && expandedNodeIds.has(scNode.id)) {
        const inputPositions = edges
          .filter((e) => e.target_id === scNode.id)
          .map((e) => positions[e.source_id])
          .filter(Boolean) as { x: number; y: number }[];

        if (inputPositions.length > 0) {
          // Inputs can straddle columns when the BFS gives one a shallower depth; only this column's rows matter.
          const colKey = String(inputPositions[0].x);
          const colYs = inputPositions.filter((p) => String(p.x) === colKey).map((p) => p.y);
          const overflowY = claimFreeY(colKey, Math.max(...colYs) + pitch, 1);
          const overflowId = `overflow-input-${scNode.id}`;
          const overflowData: OverflowNodeData = {
            remaining: inputOverflow,
            nodeHeight,
            onShowMore: () => showMoreInputs(scNode.id),
          };
          overflowNodes.push({
            id: overflowId,
            type: 'supplyChainOverflow',
            position: { x: inputPositions[0].x, y: overflowY },
            data: overflowData as unknown as Record<string, unknown>,
            draggable: false,
            style: INTERACTIVE_NODE_STYLE,
          });
          overflowEdges.push({
            id: `overflow-edge-input-${scNode.id}`,
            source: overflowId,
            target: scNode.id,
            style: overflowEdgeStyle,
          });
        }
      }

      const outputOverflow = (nodeCounts[scNode.id]?.outputs ?? 0) - (shownCounts[scNode.id]?.outputs ?? 0);

      if (outputOverflow > 0 && expandedOutputNodeIds.has(scNode.id)) {
        // Excluding root matters: a depth-1 node's input edge to root shares this source_id.
        const outputPositions = edges
          .filter((e) => e.source_id === scNode.id && e.target_id !== rootNodeId)
          .map((e) => positions[e.target_id])
          .filter(Boolean) as { x: number; y: number }[];

        if (outputPositions.length > 0) {
          const colKey = String(outputPositions[0].x);
          const colYs = outputPositions.filter((p) => String(p.x) === colKey).map((p) => p.y);
          const parentY = positions[scNode.id]?.y ?? 0;
          // Parent above root → consumers are above root → prefer above the topmost consumer; otherwise below the bottommost.
          const overflowY =
            parentY < 0
              ? claimFreeY(colKey, Math.min(...colYs) - pitch, -1)
              : claimFreeY(colKey, Math.max(...colYs) + pitch, 1);
          const overflowId = `overflow-output-${scNode.id}`;
          const overflowData: OverflowNodeData = {
            remaining: outputOverflow,
            nodeHeight,
            onShowMore: () => showMoreOutputs(scNode.id),
          };
          overflowNodes.push({
            id: overflowId,
            type: 'supplyChainOverflow',
            position: { x: outputPositions[0].x, y: overflowY },
            data: overflowData as unknown as Record<string, unknown>,
            draggable: false,
            style: INTERACTIVE_NODE_STYLE,
          });
          overflowEdges.push({
            id: `overflow-edge-output-${scNode.id}`,
            source: scNode.id,
            target: overflowId,
            style: overflowEdgeStyle,
          });
        }
      }
    }

    return {
      nodes: [...flowNodes, ...overflowNodes],
      edges: [...flowEdges, ...overflowEdges],
    };
  }, [
    nodes,
    edges,
    nodeCounts,
    shownCounts,
    rootNodeId,
    expandedNodeIds,
    expandedOutputNodeIds,
    activeNodeId,
    mode,
    selectedIndicatorId,
    scoreByNodeId,
    scoreUnit,
    scoresLoading,
    scoresError,
    onExpand,
    onCollapse,
    onExpandOutputs,
    onCollapseOutputs,
    onSelectNode,
    onViewDataset,
    onCopyScore,
    showMoreInputs,
    showMoreOutputs,
    overflowEdgeColor,
  ]);
};
